import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useMemo, useRef, useContext } from 'react';
import { Modal, Fade } from '@material-ui/core';
import cn from 'classnames';
import { getTestIds } from '../../utils/TestIds';
import { BiModalContainer } from '../BiTracking';
import { DeviceProvider } from '../Device';
import styles from './OverlayContext.module.scss';
export const GetModalContainerTID = (parentTestId)=>getTestIds(parentTestId);
const IsInModalContext = /*#__PURE__*/ React.createContext(false);
export const useIsInModal = ()=>useContext(IsInModalContext);
const getTransitionDuration = (isTestMode)=>isTestMode ? 0 : 225;
const DefaultModalWrapper = (param)=>{
    let { children } = param;
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: children
    });
};
const makeModalCustomBackdrop = (isTestMode)=>{
    return (param)=>{
        let { open, onClick, modal } = param;
        const FadeComp = Fade;
        return /*#__PURE__*/ _jsx(FadeComp, {
            timeout: getTransitionDuration(isTestMode),
            in: open,
            onClick: onClick,
            children: /*#__PURE__*/ _jsx("div", {
                className: cn(styles.modalBackdrop, {
                    [styles.modalBg]: !modal.transparentBackdrop
                })
            })
        });
    };
};
export function ModalContainer(param) {
    let { modal, ModalWrapper = DefaultModalWrapper, isTestMode, disableEnforceFocus, triggerClose, onModalExited } = param;
    const { ModalContainerTID } = useMemo(()=>GetModalContainerTID(), []);
    const [returnValue, setReturnValue] = useState(undefined);
    const canDismissModal = useRef();
    const registerCanDismiss = (cb)=>canDismissModal.current = cb;
    const BackdropComponent = useMemo(()=>makeModalCustomBackdrop(isTestMode), [
        isTestMode
    ]);
    const dismissModal = async function dismissModal(value) {
        let { force } = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
        if (canDismissModal.current && !force) {
            const confirmed = await canDismissModal.current();
            if (!confirmed) return;
        }
        setReturnValue(value);
        triggerClose(modal.id);
    };
    modal.dismissModal = dismissModal;
    const modalProps = {
        dismissModal,
        registerCanDismiss
    };
    const wrapWithDeviceProvider = (children)=>modal.device ? /*#__PURE__*/ _jsx(DeviceProvider, {
            value: modal.device,
            children: children
        }) : children;
    return /*#__PURE__*/ _jsx(Modal, {
        open: modal.isOpen,
        disableEnforceFocus: disableEnforceFocus || modal.disableEnforceFocus,
        closeAfterTransition: true,
        BackdropComponent: BackdropComponent,
        BackdropProps: {
            modal
        },
        onClose: (e, reason)=>{
            if (reason === 'escapeKeyDown' && modal.closeOnEscapeKey) {
                dismissModal();
            }
        },
        children: /*#__PURE__*/ _jsx(Fade, {
            timeout: getTransitionDuration(isTestMode),
            in: modal.isOpen,
            onExited: ()=>{
                modal.resolve(returnValue);
                onModalExited(modal.id);
            },
            children: /*#__PURE__*/ _jsxs("div", {
                className: styles.modalContainer,
                style: _object_spread_props(_object_spread({}, modal.theme), {
                    pointerEvents: modal.isOpen ? 'auto' : 'none'
                }),
                "data-testid": ModalContainerTID.Root(),
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: styles.modalBackdrop,
                        "data-testid": ModalContainerTID.Backdrop(),
                        onClick: ()=>{
                            if (modal.closeOnClickOutside) {
                                dismissModal();
                            }
                        }
                    }),
                    wrapWithDeviceProvider(/*#__PURE__*/ _jsx(BiModalContainer, _object_spread_props(_object_spread({}, modal.noBi ? {
                        noBi: true,
                        biCallerContext: modal.biCallerContext
                    } : {
                        biName: modal.biName,
                        biData: modal.biData,
                        biCallerContext: modal.biCallerContext
                    }), {
                        children: /*#__PURE__*/ _jsx(ModalWrapper, _object_spread_props(_object_spread({}, modalProps), {
                            children: /*#__PURE__*/ _jsx(IsInModalContext.Provider, {
                                value: !modal.treatAsPage,
                                children: modal.content && (typeof modal.content === 'function' ? modal.content(modalProps) : /*#__PURE__*/ React.cloneElement(modal.content, modalProps))
                            })
                        }))
                    })))
                ]
            })
        })
    });
}
